import { Location } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { loadScript } from '@paypal/paypal-js';
import { catchError, lastValueFrom, map, throwError } from 'rxjs';
import { environment } from '../../../../../environment/environment';
import { BookingCreateUpdateModel } from '../../../../models/booking.model';
import { TrainingCategoryModel } from '../../../../models/training-category.model';
import { AlertService } from '../../../../services/alert.service';
import { BookingService } from '../../../../services/booking.service';

@Component({
  selector: 'app-choose-payment-method',
  templateUrl: './choose-payment-method.component.html',
  styleUrl: './choose-payment-method.component.scss',
  standalone: false,
})
export class ChoosePaymentMethodComponent {
  @Input() category: TrainingCategoryModel;
  @Input() totalAmount: number = 20.0;
  @Input() bookingCreateModel: BookingCreateUpdateModel;
  @Input() paypalEnabled: boolean = true;
  @Output() paymentMethodSelected = new EventEmitter<string>();
  @Output() bookingIdSet = new EventEmitter<number>();
  bookingId: number;

  constructor(
    private location: Location,
    private alertService: AlertService,
    private bookingService: BookingService
  ) {}
  public ngAfterViewInit(): void {
    // Load PayPal button after component is initialized
    this.loadPaypalButton();
  }

  public loadPaypalButton(): void {
    if (!this.paypalEnabled) {
      return;
    }

    loadScript({
      clientId: environment.paypal_client_id,
      currency: 'EUR',
    })
      .then((paypal: any) => {
        paypal
          .Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            style: {
              shape: 'rect',
              layout: 'vertical',
              color: 'silver',
              label: 'paypal',
            },
            createOrder: (data, actions) => {
              this.bookingCreateModel.payment_method = 'paypal';
              return lastValueFrom(
                this.bookingService.createBooking(this.bookingCreateModel).pipe(
                  map(response => {
                    this.bookingId = response.booking_id;
                    this.bookingIdSet.emit(response.booking_id);
                    return response.paypal_response;
                  }),
                  catchError(error => {
                    this.bookingId = null;
                    this.bookingIdSet.emit(null);
                    return throwError(() => error);
                  })
                )
              )
                .then(response => {
                  return response.id; // Return the order ID received from your backend
                })
                .catch(error => {
                  console.error('Error creating PayPal order', error);
                  throw new Error(error.error ?? 'Error creating PayPal order');
                });
            },

            onApprove: (data, actions) => {
              // Once the payment is approved, capture it
              return actions.order.capture().then(details => {
                this.bookingService
                  .confirmPaypalBooking(this.bookingId)
                  .subscribe({
                    next: response => {
                      this.paymentMethodSelected.emit('paypal');
                    },
                    error: error => {
                      console.error('Error confirming booking:', error);
                    },
                  });
              });
            },

            onCancel: () => {
              // If the user cancels the payment, you can handle it here
              if (!this.bookingId) {
                return;
              }
              this.bookingService
                .removePayPalBooking(this.bookingId)
                .subscribe({
                  next: () => {
                    this.bookingIdSet.emit(null);
                  },
                  error: error => {
                    console.error('Error removing booking:', error);
                  },
                });
            },

            onError: (err: any) => {
              // If there is an error with the PayPal button
              console.error('PayPal Button Error', err.message, err);
              if (err.message === 'not_enough_seats') {
                this.alertService.showErrorAlert(
                  'Der Kurs hat nicht mehr genug freie Plätze.'
                );
              } else if (err.message.startsWith('user_already_registered')) {
                const email = err.message.split(':')[1]?.trim();
                this.alertService.showErrorAlert(
                  `Ein Benutzer mit der E-Mail-Adresse <strong><em>${email}</em></strong> ist bereits für diesen Kurs angemeldet.`,
                  10000
                );
              } else {
                this.alertService.showErrorAlert(
                  'Bei der Zahlung mit PayPal ist ein Fehler aufgetreten.'
                );
              }
              if (this.bookingId) {
                this.bookingService
                  .removePayPalBooking(this.bookingId)
                  .subscribe({
                    next: () => {
                      this.bookingIdSet.emit(null);
                    },
                    error: error => {
                      console.error('Error removing booking:', error);
                    },
                  });
              }
            },
          })
          .render('#paypal-button-container') // Render PayPal button inside the container
          .catch((error: any) => {
            console.error('failed to render the PayPal Buttons', error);
          });
      })
      .catch(error => {
        console.error('failed to load the PayPal JS SDK script', error);
      });
  }

  public selectPaymentMethod(paymentMethod: string) {
    this.paymentMethodSelected.emit(paymentMethod);
  }

  public onGoBack() {
    this.location.back();
  }
}
